import type { CSSProperties } from 'react';
import React, { useLayoutEffect, useRef, useState } from 'react';

import { Text } from '@change/ds-core-ui';

export type Signer = {
	user?: { id?: string; displayName?: string } | null;
	createdAtRelativeLocalized: string;
};

/** Scroll speed: duration = stripWidth / PX_PER_SECOND so motion stays ~constant px/s (keyframes move -50% = one strip width). */
/** Lower = slower marquee (same value keeps speed consistent across strip widths). */
const PX_PER_SECOND = 22;
/** Floor only to avoid unusably fast / zero-duration edge cases; do not clamp high or long strips skew speed. */
const MIN_DURATION_SEC = 1;
/** Safety cap for pathological list widths (still ~30k+ px of content before this bites). */
const MAX_DURATION_SEC = 3600;
const FALLBACK_DURATION_SEC = 240;

const renderSignerItem = (signer: Signer, index: number, qaId?: string, keySuffix?: string) => (
	<div
		key={`${signer.user?.id ?? index}-${index}${keySuffix ?? ''}`}
		className="shrink-0 pt-2 pr-[11px] pb-4 pl-[11px] whitespace-nowrap"
		data-qa={qaId ? `${qaId}-item` : undefined}
	>
		<Text variant="body" size="x-small" className="font-light text-[#403A49]">
			{signer.user?.displayName || 'Anonymous'}
			{' • '}
			{signer.createdAtRelativeLocalized}
		</Text>
	</div>
);

export type SignersCarouselProps = {
	signers: readonly Signer[];
	testId?: string;
	qaId?: string;
};

export const SignersCarousel: React.FC<SignersCarouselProps> = ({ signers, testId, qaId }) => {
	const firstStripRef = useRef<HTMLDivElement>(null);
	const [durationSec, setDurationSec] = useState(FALLBACK_DURATION_SEC);

	useLayoutEffect(() => {
		const strip = firstStripRef.current;
		if (!strip || typeof ResizeObserver === 'undefined') {
			return undefined;
		}

		const updateDuration = () => {
			const widthInPixels = strip.scrollWidth;
			if (widthInPixels <= 0) {
				return;
			}
			const raw = widthInPixels / PX_PER_SECOND;
			const clamped = Math.min(MAX_DURATION_SEC, Math.max(MIN_DURATION_SEC, raw));
			setDurationSec(clamped);
		};

		updateDuration();
		const scrollResizeObserver = new ResizeObserver(updateDuration);
		scrollResizeObserver.observe(strip);
		return () => {
			scrollResizeObserver.disconnect();
		};
	}, [signers]);

	if (signers.length === 0) {
		return (
			<div className="flex h-4 w-full flex-row items-start overflow-x-hidden" data-testid={testId} data-qa={qaId} />
		);
	}

	return (
		<div
			className="group flex w-full max-w-full min-w-0 flex-row items-start overflow-x-hidden"
			data-testid={testId}
			data-qa={qaId}
		>
			<div
				className="animate-signers-carousel group-hover:paused flex shrink-0 flex-row items-start"
				style={
					{
						width: 'max-content',
						'--signers-carousel-duration': `${durationSec}s`,
					} as CSSProperties
				}
			>
				<div ref={firstStripRef} className="flex shrink-0 flex-row items-start">
					{signers.map((signer, index) => renderSignerItem(signer, index, qaId))}
				</div>
				<div aria-hidden="true" className="flex shrink-0 flex-row items-start">
					{signers.map((signer, index) => renderSignerItem(signer, index, qaId, '-clone'))}
				</div>
			</div>
		</div>
	);
};
